import React, { useEffect, useMemo } from 'react';
import { StepDialogFooter } from '../StepDialogFooter';
import { DATE_TRIGGER_STEP_COPY } from './date-trigger-step-copy';
import styles from './DateTriggerConfigDialog.module.css';

const MONTHS = Array.from({ length: 12 }, (_, i) => ({
  value: i + 1,
  monthName: new Intl.DateTimeFormat('en-US', { month: 'long' }).format(new Date(2024, i, 1)),
}));

function getDaysInMonth(month: number): number {
  // Use 2024 as a leap year so Feb 29 is selectable.
  return new Date(2024, month, 0).getDate();
}

interface DateTriggerDateSelectionStepProps {
  selectedMonth: number;
  selectedDay: number;
  customTriggerCopy: string;
  onMonthChange: (month: number) => void;
  onDayChange: (day: number) => void;
  onCustomTriggerCopyChange: (copy: string) => void;
  onCancel: () => void;
  onContinue: () => void;
}

export const DateTriggerDateSelectionStep: React.FC<DateTriggerDateSelectionStepProps> = ({
  selectedMonth,
  selectedDay,
  customTriggerCopy,
  onMonthChange,
  onDayChange,
  onCustomTriggerCopyChange,
  onCancel,
  onContinue,
}) => {
  const availableDays = useMemo(() => {
    const daysInMonth = getDaysInMonth(selectedMonth);
    return Array.from({ length: daysInMonth }, (_, i) => i + 1);
  }, [selectedMonth]);

  useEffect(() => {
    const maxDayForMonth = getDaysInMonth(selectedMonth);
    if (selectedDay > maxDayForMonth) {
      onDayChange(maxDayForMonth);
    }
  }, [selectedMonth, selectedDay, onDayChange]);

  const selectedDate = new Date(2024, selectedMonth - 1, selectedDay);
  const formattedDate = selectedDate.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' });
  const placeholderCopy = `e.g. Summer sale (${MONTHS[selectedMonth - 1].monthName} ${selectedDay})`;

  return (
    <>
      <main className={styles.dateBody}>
        <output>{formattedDate}, annually</output>
        <fieldset>
          <label>
            Month
            <select value={selectedMonth} onChange={e => onMonthChange(Number(e.target.value))}>
              {MONTHS.map(month => (
                <option key={month.value} value={month.value}>
                  {month.monthName}
                </option>
              ))}
            </select>
          </label>
          <label>
            Day
            <select value={selectedDay} onChange={e => onDayChange(Number(e.target.value))}>
              {availableDays.map(day => (
                <option key={day} value={day}>
                  {day}
                </option>
              ))}
            </select>
          </label>
        </fieldset>
        <label className={styles.triggerCopyLabel}>
          Trigger label
          <input
            type="text"
            value={customTriggerCopy}
            onChange={e => onCustomTriggerCopyChange(e.target.value)}
            placeholder={placeholderCopy}
          />
          <span>Optional — shown to customers in the automation view.</span>
        </label>
      </main>
      <StepDialogFooter
        onCancel={onCancel}
        onOK={onContinue}
        okLabel={DATE_TRIGGER_STEP_COPY.date.continueLabel}
      />
    </>
  );
};
